// Withholding-tax catalog (#4304) — mirrors Finance.API WithholdingTaxType/Code DTOs.
// Country-keyed TYPE (calculation rules) + effective-dated rate-bearing CODE. Bilingual names (Translation).
import { FormControl } from '@angular/forms';
import { ResponseTranslation } from '../Translations';

/** Income tax (ISR) vs value-added (IVA) withholding. */
export enum WithholdingTaxKind {
  IncomeTax = 1,
  ValueAdded = 2,
}

/** When the withholding is recognized — drives the (future) posting point. */
export enum WithholdingCalculationTiming {
  AtInvoice = 1,
  AtPayment = 2,
}

/** The amount the rate is applied to. */
export enum WithholdingBaseType {
  Gross = 1,
  TaxableSubtotal = 2,
  TaxAmount = 3,
}

/** Supplier residency the concept applies to. */
export enum WithholdingResidency {
  Any = 0,
  Resident = 1,
  NonResident = 2,
}

/**
 * The jurisdiction whose taxes we withhold — always Honduras today, regardless of the provider's domicile
 * (a USA/GTM provider is still withheld under HND rules; we cannot withhold Kentucky or Guatemalan taxes).
 * Mirrors Finance.API `WithholdingJurisdiction.Default`; non-editable in the UI. When sites outside
 * Honduras launch this graduates to per-site master data and stops being a constant.
 */
export const WITHHOLDING_COUNTRY_CODE = 'HND';

/** GL account reference ({ reference, number }) — mirrors Finance.Models GlAccountRefDto. */
export interface GlAccountRef {
  reference?: string | null;
  number?: string | null;
}

/** A withholding TYPE — calculation rules; owns one or more rate-bearing codes. */
export interface WithholdingTaxType {
  id?: string | null;
  countryCode: string;
  code: string;
  name: ResponseTranslation;
  description?: ResponseTranslation | null;
  taxKind: WithholdingTaxKind;
  timing: WithholdingCalculationTiming;
  baseType: WithholdingBaseType;
  isActive: boolean;
}

/** Caller-supplied fields to create/update a type (Code + CountryCode immutable once created). */
export interface WithholdingTaxTypeInsert {
  countryCode: string;
  code: string;
  name: ResponseTranslation;
  description?: ResponseTranslation | null;
  taxKind: WithholdingTaxKind;
  timing: WithholdingCalculationTiming;
  baseType: WithholdingBaseType;
  isActive: boolean;
}

/** A withholding CODE — the effective-dated rate under a type. */
export interface WithholdingTaxCode {
  id?: string | null;
  withholdingTypeReference: string;
  countryCode?: string | null;
  taxKind: WithholdingTaxKind;
  code: string;
  name: ResponseTranslation;
  description?: ResponseTranslation | null;
  rate: number;
  residency: WithholdingResidency;
  effectiveFrom: string;
  effectiveTo?: string | null;
  postingAccount?: GlAccountRef | null;
  isActive: boolean;
}

/** Caller-supplied fields to create/update a code (Code + parent type immutable once created). */
export interface WithholdingTaxCodeInsert {
  withholdingTypeReference: string;
  code: string;
  name: ResponseTranslation;
  description?: ResponseTranslation | null;
  rate: number;
  residency: WithholdingResidency;
  effectiveFrom: string;
  effectiveTo?: string | null;
  postingAccount?: GlAccountRef | null;
  isActive: boolean;
}

/** Strict reactive form for a withholding TYPE (En/Es name + description entered separately). */
export interface WithholdingTaxTypeForm {
  countryCode: FormControl<string>;
  code: FormControl<string>;
  nameEn: FormControl<string>;
  nameEs: FormControl<string>;
  descriptionEn: FormControl<string>;
  descriptionEs: FormControl<string>;
  taxKind: FormControl<WithholdingTaxKind>;
  timing: FormControl<WithholdingCalculationTiming>;
  baseType: FormControl<WithholdingBaseType>;
  isActive: FormControl<boolean>;
}

/** Strict reactive form for a withholding CODE. */
export interface WithholdingTaxCodeForm {
  withholdingTypeReference: FormControl<string>;
  code: FormControl<string>;
  nameEn: FormControl<string>;
  nameEs: FormControl<string>;
  descriptionEn: FormControl<string>;
  descriptionEs: FormControl<string>;
  rate: FormControl<number>;
  residency: FormControl<WithholdingResidency>;
  effectiveFrom: FormControl<Date | null>;
  effectiveTo: FormControl<Date | null>;
  postingAccountReference: FormControl<string | null>;
  postingAccountNumber: FormControl<string | null>;
  postingAccountName: FormControl<string | null>;
  isActive: FormControl<boolean>;
}
